import { type Layer } from "../constants";
import { type Orientation } from "../typings";
import { CircleHitbox, GroupHitbox, RectangleHitbox, type ShapeHitbox } from "../utils/hitbox";
import { Angle } from "../utils/math";
import { DefinitionType } from "../utils/objectDefinitions";
import { FloorNames } from "../utils/terrain";
import { Vec } from "../utils/vector";
import { type BuildingDefinition, type BuildingGlass, type BuildingImageDefinition, type BuildingObstacle } from "./buildings";
import { BLOCK_LADDER } from "./roofObstacles";

/*
    The roofs (1.08, the owner's picks on the roofs page, 29 October 2026, of every roof Fargo has; the owner, earlier
    that day: "we'll go through roofs and add ladders or whatever to go down to the floor below, those who ara unable
    to have ladder and trapdoor, like house roofs that tilt, will use the tech you built"; README "The roofs (1.08)").

    A flat roof one stands on is a deck (`BuildingDefinition.openAir`): a building at its building's place, a floor
    higher than its top one (a sub-building of it), its floors where one stands, its walls the parapet round its edge
    and what stands on the roof (the air handlers, the dome, the dishes: drawn in the roof's own
    pictures), its hatches and ladders (roofObstacles.ts). The picks: hatches down to the floor under the blocks', the
    headquarters', the museum's (into its attic) and, as suggested, Pool Day's shower side and lobby; a ladder down the
    lighthouse's watch post (its tower's glided off) and the refinery; the police station's two hatches and its
    skylights, which give way to whoever lands or walks on them ("glash smash if landed or walking on it, 2 trapdoors
    to take down"); the cell block's hatches to its floor and ladders to the ground ("mutlipe trapdoors that take to
    lower floor, also ladders to go to the bottom immediately"). Every other roof is glided off, as before; the glass in
    one comes down through ("if glash smash": the houses', the blue house's, the tavern's, the mansions' lantern, "if
    glash smash and go down, no damage of course"; Pool Day's halls' and shower side's).

    The glass (`BuildingDefinition.glass`) is the building's whose roof shows it (the top floor's, under a deck): a pane
    smashed is drawn broken in that roof (buildings/roofs/<name>_broken, tools/fargo/roofs.py's PANES: 10 px a unit, a
    unit's margin round the pane) and is a hole. Every number here is the roof's picture's: its generator's (police.py,
    pool.py, mansions.py, museum.py), or measured off it (the cell block's, the blocks', the headquarters', the
    refinery's, the lighthouse's, the houses').
*/

const box = (x0: number, y0: number, x1: number, y1: number): RectangleHitbox =>
    new RectangleHitbox(Vec(Math.min(x0, x1), Math.min(y0, y1)), Vec(Math.max(x0, x1), Math.max(y0, y1)));

const disc = (x: number, y: number, radius: number): CircleHitbox => new CircleHitbox(radius, Vec(x, y));

/** the parapet round a deck x0, y0 to x1, y1, `width` thick, outside it */
const parapet = (x0: number, y0: number, x1: number, y1: number, width: number): RectangleHitbox[] => [
    box(x0 - width, y0 - width, x1 + width, y0),
    box(x0 - width, y1, x1 + width, y1 + width),
    box(x0 - width, y0, x0, y1),
    box(x1, y0, x1 + width, y1)
];

/** a roof's deck: where one stands (`floors`), what's solid on it (its parapet and pieces), its ways down */
function roofDeck(idString: string, name: string, floors: readonly RectangleHitbox[], solid: readonly ShapeHitbox[], obstacles: readonly BuildingObstacle[]): BuildingDefinition {
    const boxes = [...floors, ...solid].map(hitbox => hitbox.toRectangle());
    return {
        idString,
        name,
        defType: DefinitionType.Building,
        openAir: true,
        spawnHitbox: new RectangleHitbox(
            Vec(Math.min(...boxes.map(b => b.min.x)), Math.min(...boxes.map(b => b.min.y))),
            Vec(Math.max(...boxes.map(b => b.max.x)), Math.max(...boxes.map(b => b.max.y)))
        ),
        hitbox: new GroupHitbox(...solid),
        // (what flies off its parapet when it's hit, and the sound of it: 0.98.1, the decks had none, and the page drew
        // its missing picture's purple square for every shot or punch at a parapet)
        material: "stone",
        particle: "roof_deck_particle",
        floors: floors.map(hitbox => ({ type: FloorNames.Stone, hitbox })),
        obstacles
    };
}

/** the size (units) of each pane's smashed picture's glass (tools/fargo/roofs.py PANES) */
export const PANE_PICTURES = {
    police_skylight_broken: [34, 30],
    prison_skylight_broken: [10.3, 9.2],
    museum_skylight_broken: [36, 46],
    museum_front_skylight_broken: [52, 40],
    mansion_lantern_broken: [26, 36],
    house_skylight_broken: [17.2, 15.4],
    blue_house_skylight_broken: [11.7, 11.1],
    tavern_skylight_broken: [29.2, 20],
    pool_skylight_broken: [20, 4.4],
    pool_shower_skylight_broken: [12, 10]
} as const satisfies Record<string, readonly [number, number]>;

/** a pane of a roof's glass x0, y0 to x1, y1, smashed drawn `broken` (its picture stretched to the pane), `below` */
const pane = (x0: number, y0: number, x1: number, y1: number, broken: keyof typeof PANE_PICTURES, below = 0): BuildingGlass => {
    const [w, h] = PANE_PICTURES[broken];
    const hitbox = box(x0, y0, x1, y1);
    return {
        hitbox,
        below,
        broken,
        position: Vec((x0 + x1) / 2, (y0 + y1) / 2),
        scale: Vec(2 * (hitbox.max.x - hitbox.min.x) / w, 2 * (hitbox.max.y - hitbox.min.y) / h)
    };
};

/** a hatch's picture in a roof (`key`: the steel one, or the cell block's wooden one) */
const hatchPicture = (x: number, y: number, key = "roof_hatch"): BuildingImageDefinition => ({ key, position: Vec(x, y) });

/** a ladder's top's picture over a roof's edge, the ladder on a wall facing `orientation`'s way (0: south) */
const ladderTopPicture = (x: number, y: number, orientation: Orientation = 0, key = "roof_ladder_top"): BuildingImageDefinition =>
    orientation === 0
        ? { key, position: Vec(x, y) }
        : { key, position: Vec(x, y), rotation: Angle.orientationToRotation(orientation) };

/** a ladder down a wall facing `orientation`'s way: its top on the deck at `top`, its foot on the ground at `foot` */
const ladder = (top: readonly [number, number], foot: readonly [number, number], orientation: Orientation, below = -2): BuildingObstacle[] => [
    { idString: "roof_ladder_top", position: Vec(...top), rotation: orientation },
    { idString: "roof_ladder", position: Vec(...foot), rotation: orientation, layer: below }
];

/**
 * a hatch in the deck at (`x`, `y`), turned with its building (never at random: its way out is where it is), and the
 * ladder under it on the floor below (1.09: up it, through the hatch, onto the deck: no roof is a one-way place). Down
 * it, you come out `HATCH_OUT` from the ladder's foot, beside its picture: south of it (`turn` 0), or the way `turn`
 * turns that (2 north, where the room's furniture stands south of it, the guard hall's desk; 1 east and 3 west, a
 * ladder by a west or an east wall, 0.98.1). The ladder is turned the same way (0.98.1: a loft ladder let down from
 * the hatch, its top at the picture's top, its feet towards the way out: by a wall, its top against it), `ladderIn`
 * from the hatch's middle towards its top (a hatch kept clear of a parapet over the wall the ladder stands against)
 */
const hatch = (x: number, y: number, idString = "roof_hatch", turn: Orientation = 0, ladderIn = 0): BuildingObstacle[] => [
    { idString, position: Vec(x, y), rotation: turn },
    { idString: "hatch_ladder", position: Vec.addAdjust(Vec(x, y), Vec(0, -ladderIn), turn), rotation: turn, layer: -2 }
];

// ---------------- the police station's hall (its own units; police.py's station_roof) ----------------
export const POLICE_DECK = { idString: "police_station_hall_deck", position: Vec(0, 0), layer: 2 } as const;
export const POLICE_GLASS: readonly BuildingGlass[] = [
    pane(-60, -40, -26, -10, "police_skylight_broken"),
    pane(-10, -40, 24, -10, "police_skylight_broken"),
    pane(-60, 4, -26, 32, "police_skylight_broken"),
    pane(-10, 4, 24, 32, "police_skylight_broken")
];
const POLICE_HATCHES = [[64, -36], [-30, 60]] as const;
export const POLICE_ROOF_PIECES: readonly BuildingImageDefinition[] = POLICE_HATCHES.map(([x, y]) => hatchPicture(x, y));

// ---------------- the cell block (its roof's picture, buildings/prison/prison_block_ceiling) ----------------
export const PRISON_BLOCK_DECK = { idString: "prison_block_deck", position: Vec(0, 0), layer: 2 } as const;
export const PRISON_BLOCK_GLASS: readonly BuildingGlass[] = ([
    [-89.4, -79.1], [-78.3, -68.1], [-67.3, -57.0], [-56.2, -46.0], [-45.2, -34.9],
    [-34.1, -23.8], [-23.0, -12.8], [-12.0, -1.7], [-0.9, 9.3], [10.1, 20.4]
] as const).map(([x0, x1]) => pane(x0, -4.6, x1, 4.6, "prison_skylight_broken"));
export const PRISON_BLOCK_ROOF_PIECES: readonly BuildingImageDefinition[] = [
    hatchPicture(-99, 0, "prison_roof_hatch"),
    hatchPicture(80, 0, "prison_roof_hatch"),
    ladderTopPicture(-40, 41.2, 0),
    ladderTopPicture(107.2, -25, 1)
];

// ---------------- the prison's lighthouse's watch post (lighthouse.py's roof) ----------------
export const LIGHTHOUSE_DECK = { idString: "prison_lighthouse_deck", position: Vec(0, 0), layer: 2 } as const;
export const LIGHTHOUSE_ROOF_PIECES: readonly BuildingImageDefinition[] = [ladderTopPicture(21, -2, 1)];

// ---------------- the apartment blocks (their roofs' pictures, buildings/apartment/apartment_roof_*) ----------------
export const APARTMENT_DECK = { idString: "apartment_block_deck", position: Vec(0, 0), layer: 8 } as const;
/**
 * the long ladder up the block's north end wall (0.98.1, the owner: "add a ladder but it takes 8 seconds to climb al
 * lthe way down/up, animate it well"): along the wall between two of its windows (`x`), the wall's outer face (`wall`:
 * the roof's picture's edge too), its top over the parapet on the roof's picture, its bracket on the coping
 */
export const APARTMENT_LADDER = { x: -9, wall: -105 } as const;
export const APARTMENT_ROOF_PIECES: readonly BuildingImageDefinition[] = [
    ladderTopPicture(APARTMENT_LADDER.x, APARTMENT_LADDER.wall + 1.75, 2, "block_ladder_top")
];
/**
 * the blocks' roof (tools/apartment/roof.py), in two halves that overlap a little at the block's middle, and the long
 * ladder's top: every floor's ceiling and, since 0.98.2, the deck's floor too, so the roof shows round its opening to
 * someone on the flight up onto it (`apt_roof_stair`), as the attic's boards do round the museum's staff stairs
 */
export const APARTMENT_ROOF: readonly BuildingImageDefinition[] = [
    { key: "apartment_roof_north", position: Vec(4.5, -52.375) },
    { key: "apartment_roof_south", position: Vec(4.5, 52.375) },
    ...APARTMENT_ROOF_PIECES
];
/**
 * the opening in the roof over the top floor's flight up onto it (0.98.2: the flight's 34 by 19, x -17 to 17, y 1 to
 * 20), railed round on the deck but at the flight's top (west); and the air conditioners north of it, where the stair
 * head stood from 0.81 to 0.98.1 (x -19 to 19, y -19 to 19, with them on it), as the roof's picture draws them
 */
export const APARTMENT_STAIRS_OPENING = { x: 0, y: 10.5, width: 34, height: 19 } as const;
const APARTMENT_AIR_CONDITIONERS = [[-13, -13], [5, -13]] as const;

// ---------------- the headquarters (its own units; its second floor stands at 5.6, -0.6 in them) ----------------
export const HEADQUARTERS_DECK = { idString: "headquarters_deck", position: Vec(0, 0), layer: 4 } as const;
/**
 * its two hatches (0.98.1, the owner: "on the headquaters, the placemernt for the hatches in the bottom are bad, make
 * them in the side of the walls"): their ladders against the west walls of the corridor and of the meeting room under
 * them (1.08's stood in the corridor's middle and by the meeting table), the way out east of each
 */
const HEADQUARTERS_HATCHES = [[-54.4, -45, 0], [-66.4, -20, 1.2]] as const;
/** on the second floor's roof, in the second floor's own units */
export const HEADQUARTERS_ROOF_PIECES: readonly BuildingImageDefinition[] = HEADQUARTERS_HATCHES.map(([x, y]) => hatchPicture(x - 5.6, y + 0.6));

// ---------------- the dinosaur museum (its own units; museum.py's museum_roof) ----------------
export const MUSEUM_DECK = { idString: "dino_museum_deck", position: Vec(0, 0), layer: 4 } as const;
/** on the attic's roof (the one shown over the hall and the attic), down onto the attic's boards */
export const MUSEUM_GLASS: readonly BuildingGlass[] = [
    pane(-113, -82, -77, -36, "museum_skylight_broken"),
    pane(77, -82, 113, -36, "museum_skylight_broken"),
    pane(-113, -8, -77, 26, "museum_skylight_broken"),
    pane(77, -8, 113, 26, "museum_skylight_broken"),
    pane(-118, 46, -66, 86, "museum_front_skylight_broken"),
    pane(66, 46, 118, 86, "museum_front_skylight_broken")
];
const MUSEUM_HATCHES = [[-30, 48], [30, -68]] as const;
export const MUSEUM_ROOF_PIECES: readonly BuildingImageDefinition[] = MUSEUM_HATCHES.map(([x, y]) => hatchPicture(x, y));

// ---------------- the refinery (its roof's pictures, buildings/refinery/refinery_ceiling_*) ----------------
export const REFINERY_DECK = { idString: "refinery_deck", position: Vec(0, 0), layer: 2 } as const;
export const REFINERY_ROOF_PIECES: readonly BuildingImageDefinition[] = [ladderTopPicture(3, -6.5, 0)];

// ---------------- Pool Day (pool.py's roofs) ----------------
export const POOL_SHOWERS_DECK = { idString: "fargo_pool_showers_deck", position: Vec(0, 0), layer: 2 } as const;
export const POOL_SHOWERS_GLASS: readonly BuildingGlass[] = [-30, -10, 10, 30].map(y => pane(-6, y - 5, 6, y + 5, "pool_shower_skylight_broken"));
export const POOL_SHOWERS_ROOF_PIECES: readonly BuildingImageDefinition[] = [hatchPicture(-8.5, 0)];
export const POOL_LOBBY_DECK = { idString: "fargo_pool_lobby_deck", position: Vec(0, 0), layer: 2 } as const;
// (its hatch west of the POOL painted on the roof, over the hall by the arches onto the deck)
export const POOL_LOBBY_ROOF_PIECES: readonly BuildingImageDefinition[] = [hatchPicture(-7, -12)];
/** the halls' skylights along their ridges (both halls) */
export const POOL_HALL_GLASS: readonly BuildingGlass[] = [-60, -30, 0, 30, 60].map(x => pane(x - 10, -2.2, x + 10, 2.2, "pool_skylight_broken"));

// ---------------- the roofs glided off, with glass to come down through ----------------
export const RED_HOUSE_GLASS: readonly BuildingGlass[] = [pane(-8.6, -7.95, 8.6, 7.45, "house_skylight_broken")];
export const RED_HOUSE_V2_GLASS: readonly BuildingGlass[] = [pane(-9.2, -7.95, 8, 7.45, "house_skylight_broken")];
export const BLUE_HOUSE_GLASS: readonly BuildingGlass[] = [
    pane(-24.7, -24.3, -13, -13.2, "blue_house_skylight_broken"),
    pane(-24.7, -9.5, -13, 1.5, "blue_house_skylight_broken"),
    pane(-24.7, 4.95, -13, 16, "blue_house_skylight_broken")
];
export const TAVERN_GLASS: readonly BuildingGlass[] = [
    pane(-60.5, -40.1, -31.3, -20.1, "tavern_skylight_broken"),
    pane(-60.3, 2.9, -31.1, 22.9, "tavern_skylight_broken")
];
/** the mansions' lantern, on their upper floors' roof: down through the hall's well to the hall's floor */
export const MANSION_LANTERN_GLASS: readonly BuildingGlass[] = [pane(-13, -18, 13, 18, "mansion_lantern_broken", -2)];

// ---------------- the decks ----------------
export const ROOF_DECKS: readonly BuildingDefinition[] = [
    roofDeck(
        POLICE_DECK.idString, "Police Station Roof",
        [box(-123.4, -109.4, 123.4, 109.4)],
        [
            ...parapet(-123.4, -109.4, 123.4, 109.4, 2.6),
            // the air handlers, the solar panels, the radio mast and its dishes, the vents
            box(-109, -91, -91, -81), box(-55, -89, -33, -79), box(-2, -88.5, 14, -79.5), box(-108, 75.5, -92, 84.5),
            box(-122, -40, -82, 30),
            disc(110, -92, 2.2), disc(96, -96, 3.2), disc(118, -74, 2.6),
            ...([[-118, -60], [-84, -60], [-20, -100], [40, -100], [70, 70], [110, 70], [20, 70], [-60, 70], [-118, 104]] as const).map(([x, y]) => disc(x, y, 1.3))
        ],
        POLICE_HATCHES.flatMap(([x, y]) => hatch(x, y))
    ),
    roofDeck(
        PRISON_BLOCK_DECK.idString, "Cell Block Roof",
        [box(-106.4, -40.4, 106.4, 40.4)],
        [
            ...parapet(-106.4, -40.4, 106.4, 40.4, 1.6),
            // the air handlers, the vents' boxes, the antenna
            box(-83.5, -31, -66.5, -19), box(-29.5, -31, -12.5, -19), box(-56.5, 19, -39.5, 31), box(-2.5, 19, 14.5, 31),
            box(60.5, -28.5, 79.5, -15.5), box(82.5, 15.5, 101.5, 28.5),
            ...([[-98.6, -32.6], [-98.6, 27.4], [-54.6, -30.6], [-0.6, -30.6], [-77.6, 24.4], [-23.6, 24.4], [15.4, -32.6], [15.4, 27.4]] as const)
                .map(([x, y]) => box(x, y, x + 5.2, y + 5.2)),
            disc(41, 22, 3)
        ],
        [
            // the one its roof draws over the guard hall, one over each end of the corridor
            ...hatch(40, -24, "prison_roof_hatch", 2), ...hatch(-99, 0, "prison_roof_hatch"), ...hatch(80, 0, "prison_roof_hatch"),
            // down the yard's side and the east end, to the ground
            ...ladder([-40, 38.4], [-40, 43.5], 0),
            ...ladder([104.4, -25], [109.5, -25], 1)
        ]
    ),
    roofDeck(
        LIGHTHOUSE_DECK.idString, "Watch Post Roof",
        [box(-20, -12, 20, 31)],
        [...parapet(-20, -12, 20, 31, 2), disc(13, -9, 1.3), disc(-15, 25, 1.3), box(3.5, 18, 6.8, 24.2)],
        ladder([18, -2], [23.5, -2], 1)
    ),
    {
        ...roofDeck(
            APARTMENT_DECK.idString, "Apartment Block Roof",
            [box(-33.5, -103.5, 33.4, 103.5)],
            [
                ...parapet(-33.5, -103.5, 33.4, 103.5, 1.5),
                // the air conditioners by the stairs' opening, the vents' boxes, the pigeon loft, the dishes, the vents,
                // the mast
                ...APARTMENT_AIR_CONDITIONERS.map(([x, y]) => box(x, y, x + 8, y + 5)),
                box(-27, -59, -19, -52), box(11, -59, 19, -52), box(-27, 52, -19, 59), box(11, 52, 19, 59),
                box(-29, 24, -15, 34),
                disc(22.5, -82, 2.7), disc(-27, -35, 2.6), disc(25, -35, 2.7), disc(25, 77, 2.7), disc(-27, 84, 2.7),
                disc(0, -88.5, 1.2), disc(0, -45, 1.2), disc(0, 44, 1.2), disc(0, 88, 1.2), disc(0, 61, 1.2)
            ],
            [
                // (0.98.2) the railing round the opening over the top floor's flight up (buildings.ts `apt_roof_stair`),
                // open at the flight's top: walked up onto the roof and down again, as the museum's attic stairs are. From
                // 1.08 to 0.98.1 a stair head stood over it, F at its door taking you down and at a picture of stairs on
                // the landing up (`roof_stair_door`, `roof_stair_up`)
                { idString: "apt_roof_rail", position: Vec(APARTMENT_STAIRS_OPENING.x, APARTMENT_STAIRS_OPENING.y), rotation: 0 },
                // (0.98.1) the long ladder up the north end wall: its top on the roof, its foot on the ground, 8 s either way
                { idString: "block_ladder_top", position: Vec(APARTMENT_LADDER.x, APARTMENT_LADDER.wall + BLOCK_LADDER.top), rotation: 2 },
                // (its foot four floors and their stairs' layers below the deck's, on the ground: a building's obstacle's
                // layer is its own plus the building's)
                { idString: "block_ladder", position: Vec(APARTMENT_LADDER.x, APARTMENT_LADDER.wall - BLOCK_LADDER.foot), rotation: 2, layer: -APARTMENT_DECK.layer as Layer }
            ]
        ),
        // (0.98.2) its floor the roof's own picture: seen from the flight up onto it, round its opening
        floorImages: APARTMENT_ROOF
    },
    roofDeck(
        HEADQUARTERS_DECK.idString, "Headquarters Roof",
        [box(-58, -105.5, 69.5, -30), box(-69.5, -32.8, -25.5, 35.3)],
        [
            box(-60, -107, 71, -105.5), box(69.5, -107, 71, -28.2), box(-25.5, -30, 71, -28.2), box(-25.5, -30, -23.5, 37),
            box(-71, 35.3, -23.5, 37), box(-71, -34.5, -69.5, 37), box(-71, -34.5, -58, -32.8), box(-59.7, -107, -58, -32.8),
            // the air handlers, the ducts, the grilles, the roof box over the meeting room
            box(-29.1, -99.7, -11.2, -81.2), box(-29.1, -68.1, -11.2, -49.7), box(48.9, -99.7, 66.8, -81.2),
            box(-2.3, -78.8, 5.1, -34), box(-39.4, -41.2, 5.1, -33.7),
            box(13.1, -78.8, 20.5, -34), box(54.8, -78.8, 62.2, -34), box(13.1, -41.2, 62.2, -33.7),
            box(-55.2, -104.2, -46, -95.2), box(-55.2, -93.5, -46, -84.4),
            box(-60.7, -15, -34, 12.3)
        ],
        // over the meeting room by its west wall, and over the corridor by its west wall (0.98.1: by the walls, the ladders'
        // tops against them, the way out east)
        HEADQUARTERS_HATCHES.flatMap(([x, y, ladderIn]) => hatch(x, y, "roof_hatch", 1, ladderIn))
    ),
    roofDeck(
        MUSEUM_DECK.idString, "Museum Roof",
        [box(-127.4, -92.4, 127.4, 92.4)],
        [
            ...parapet(-127.4, -92.4, 127.4, 92.4, 2.6),
            // the dome on its drum, the air handlers, the vents
            disc(0, -11, 46.5),
            box(-46, -82.5, -30, -73.5), box(-1, -85, 21, -75),
            ...([[-52, -86], [36, -86], [48, -70], [-20, -68], [-120, 86], [120, 86], [-60, 50], [60, 50]] as const).map(([x, y]) => disc(x, y, 1.4))
        ],
        MUSEUM_HATCHES.flatMap(([x, y]) => hatch(x, y))
    ),
    roofDeck(
        REFINERY_DECK.idString, "Refinery Roof",
        [box(-48.5, -35.2, 48.75, -7.55), box(-48.5, -7.55, -17, 35.25)],
        [
            box(-50.5, -37, 50.75, -35.2), box(6.5, -35.2, 20, -33.3), box(48.75, -37, 50.75, -5.5), box(-17, -7.55, 50.75, -5.5),
            box(-17, -7.55, -15, 37.25), box(-50.5, 35.25, -15, 37.25), box(-50.5, -37, -48.5, 37.25),
            // the turbine vents, the fans, the louvres, the vent pipes
            disc(-23.5, -28.5, 5), disc(-23.5, -14.1, 5),
            disc(-3.5, -28.9, 3.1), disc(-3.5, -20.5, 3.1), disc(43, -14.5, 3.1), disc(-30.5, 0.6, 3.1), disc(-23, 0.6, 3.1),
            box(-15, -15.2, 0, -9.4), box(26.8, -29.2, 32.6, -14), box(-34.3, 10.9, -19.1, 16.8), box(-34.3, 19, -19.1, 24.8),
            box(6.4, -30.6, 9.8, -11.9), box(13.3, -30.6, 16.7, -11.9), box(20.1, -30.6, 23.5, -11.9),
            disc(-19.4, 32.7, 1.8)
        ],
        // down the hall's south wall to the yard
        ladder([3, -9.55], [3, -4], 0)
    ),
    roofDeck(
        POOL_SHOWERS_DECK.idString, "Shower Side Roof",
        [box(-12.8, -42.8, 12.8, 42.8)],
        [...parapet(-12.8, -42.8, 12.8, 42.8, 1.2), disc(8, -38, 1.2), disc(8, 38, 1.2)],
        hatch(-8.5, 0)
    ),
    roofDeck(
        POOL_LOBBY_DECK.idString, "Lobby Roof",
        [box(-12.8, -42.8, 12.8, 42.8)],
        [...parapet(-12.8, -42.8, 12.8, 42.8, 1.2), box(-4.5, -39, 4.5, -33), disc(-7, 36, 1.2)],
        hatch(-7, -12)
    )
];
